import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { GAME_CONFIGS, type RoleType } from '../types/game'
import { 
  nominateTeam, 
  voteForTeam, 
  getVoteStatus,
  executeQuest,
  assassinateMerlin,
  startNightPhase,
  merlinReveal,
  percivalReveal,
  endNightPhase
} from '../lib/game-api'
import { supabase, waitForSupabaseAuthReady } from '../lib/supabase'

interface Player {
  emp_id: string
  seat_number: number
  role: RoleType
  is_alive: boolean
  name?: string
}

interface GameSession {
  id: string
  room_id: string
  current_round: number
  round_state: 'selecting_leader' | 'nominating_team' | 'voting_team' | 'executing_quest' | 'round_complete' | 'game_over'
  quest_number: number
  fail_count: number
  success_count: number
  leader_emp_id: string | null
  leader_index: number
  team_member_ids: string[] | null
  vote_results: Record<string, boolean> | null
  quest_results: Record<string, 'success' | 'fail'> | null
  current_phase?: string
  current_leader_id?: number
  game_number?: number
  winner_faction?: string
  room_leader_id?: string
}

interface Room {
  id: string
  name: string
  role_config: any
}

interface VoteStatus {
  approve_count: number
  reject_count: number
  total_votes: number
  total_players: number
  vote_details?: Record<string, boolean>
}

interface GameLogEntry {
  id: string
  timestamp: string
  event: string
  description: string
}

export default function OnlineGameProgress() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [session, setSession] = useState<GameSession | null>(null)
  const [room, setRoom] = useState<Room | null>(null)
  const [players, setPlayers] = useState<Player[]>([])
  const [myEmpId, setMyEmpId] = useState<string | null>(null)
  const [myRole, setMyRole] = useState<RoleType | null>(null)
  
  // Team nomination state
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([])
  const [actionLoading, setActionLoading] = useState(false)
  const [actionMessage, setActionMessage] = useState<string | null>(null)
  
  // Vote state
  const [voteStatus, setVoteStatus] = useState<VoteStatus | null>(null)
  const [hasVoted, setHasVoted] = useState(false)
  
  // Game log state
  const [gameLogs, setGameLogs] = useState<GameLogEntry[]>([])
  
  // Assassination target selection
  const [selectedAssassinationTarget, setSelectedAssassinationTarget] = useState<string | null>(null)

  useEffect(() => {
    if (!sessionId) {
      setError('游戏会话 ID 缺失')
      setLoading(false)
      return
    }

    loadGameData()
    const channel = setupRealtimeSubscription()

    // Poll vote status when in voting phase
    let pollInterval: NodeJS.Timeout | null = null
    if (session?.round_state === 'voting_team') {
      pollInterval = setInterval(async () => {
        await loadVoteStatus()
      }, 2000)
    }

    return () => {
      if (channel) {
        supabase.removeChannel(channel)
      }
      if (pollInterval) {
        clearInterval(pollInterval)
      }
    }
  }, [sessionId, session?.round_state])

  function addGameLog(event: string, description: string) {
    const newLog: GameLogEntry = {
      id: `${Date.now()}-${Math.random()}`,
      timestamp: new Date().toLocaleTimeString('zh-CN'),
      event,
      description
    }
    setGameLogs(prev => [newLog, ...prev].slice(0, 20)) // Keep last 20 logs
  }

  async function loadGameData() {
    try {
      await waitForSupabaseAuthReady()
      
      // Get current user info from URL params or storage
      const urlParams = new URLSearchParams(window.location.search)
      const empId = urlParams.get('emp_id') || localStorage.getItem('dingtalk_emp_id')
      
      if (!empId) {
        setError('无法获取用户信息，请重新进入游戏')
        setLoading(false)
        return
      }

      setMyEmpId(empId)

      // Load session data
      const { data: sessionData, error: sessionError } = await supabase
        .from('online_game_sessions_cce0d86f_8f5')
        .select('*')
        .eq('id', sessionId)
        .single()

      if (sessionError || !sessionData) {
        setError('游戏会话不存在')
        setLoading(false)
        return
      }

      const oldSession = session
      setSession(sessionData as GameSession)

      // Add game log based on state changes
      if (oldSession && sessionData) {
        if (oldSession.round_state !== sessionData.round_state) {
          addGameLog('阶段变更', `从 ${oldSession.round_state} 进入 ${sessionData.round_state}`)
        }
        if (sessionData.round_state === 'executing_quest' && sessionData.quest_number !== oldSession.quest_number) {
          addGameLog('任务开始', `第 ${sessionData.current_round} 轮 - 任务 ${sessionData.quest_number}`)
        }
      }

      // Load room data
      const { data: roomData } = await supabase
        .from('online_game_rooms_cce0d86f_8f5')
        .select('*')
        .eq('id', sessionData.room_id)
        .single()

      if (roomData) {
        setRoom(roomData as Room)
      }

      // Load player states with roles (roles are already assigned by RPC function)
      const { data: playerStates } = await supabase
        .from('online_game_player_states_cce0d86f_8f5')
        .select('emp_id, seat_number, player_role, faction, is_alive, player_nickname')
        .eq('session_id', sessionId)
        .eq('is_deleted', 'n')
        .order('seat_number')

      if (playerStates) {
        // Map database fields to frontend interface
        const mappedPlayers = playerStates.map(p => ({
          emp_id: p.emp_id!,
          seat_number: p.seat_number || 0,
          role: (p.player_role as RoleType) || 'loyalServant',
          is_alive: p.is_alive ?? true,
          name: p.player_nickname
        }))
        setPlayers(mappedPlayers)

        // Find my role (only show to current user)
        const myPlayer = mappedPlayers.find(p => p.emp_id === empId)
        if (myPlayer) {
          setMyRole(myPlayer.role)
        }
      }

      // Load vote status if in voting phase
      if (sessionData.round_state === 'voting_team') {
        await loadVoteStatus()
      }

      setLoading(false)
    } catch (err) {
      console.error('Failed to load game data:', err)
      setError('加载游戏数据失败')
      setLoading(false)
    }
  }

  async function loadVoteStatus() {
    if (!sessionId) return
    
    const result = await getVoteStatus(parseInt(sessionId))
    if (result.success && result.data) {
      setVoteStatus({
        approve_count: result.data.approve_count,
        reject_count: result.data.reject_count,
        total_votes: result.data.total_votes,
        total_players: result.data.total_players,
        vote_details: result.data.vote_details as any
      })
      
      // Check if I have voted
      if (myEmpId && result.data.vote_details) {
        setHasVoted(myEmpId in result.data.vote_details)
      }
    }
  }

  // Roles are now assigned by the backend RPC function
  // No need for frontend role assignment logic

  function setupRealtimeSubscription() {
    const channel = supabase
      .channel(`game-session-${sessionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'online_game_sessions_cce0d86f_8f5',
          filter: `id=eq.${sessionId}`
        },
        (payload) => {
          if (payload.eventType === 'UPDATE' && payload.new) {
            setSession(payload.new as GameSession)
            
            // Reload vote status if phase changed to voting
            if (payload.new.round_state === 'voting_team') {
              loadVoteStatus()
            }
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'online_game_player_states_cce0d86f_8f5',
          filter: `session_id=eq.${sessionId}`
        },
        async (payload) => {
          // Reload player states
          const { data: playerStates } = await supabase
            .from('online_game_player_states_cce0d86f_8f5')
            .select('emp_id, seat_number, player_role, faction, is_alive, player_nickname')
            .eq('session_id', sessionId)
            .eq('is_deleted', 'n')
            .order('seat_number')

          if (playerStates) {
            const mappedPlayers = playerStates.map(p => ({
              emp_id: p.emp_id!,
              seat_number: p.seat_number || 0,
              role: (p.player_role as RoleType) || 'loyalServant',
              is_alive: p.is_alive ?? true,
              name: p.player_nickname
            }))
            setPlayers(mappedPlayers)
          }
        }
      )
      .subscribe()

    return channel
  }

  function getQuestNumber(round: number): number {
    const playerCount = players.length
    if (playerCount < 5 || playerCount > 13) return 3
    
    const quests = [
      [3, 4, 4, 5, 5], // 5-6 players
      [3, 4, 4, 5, 5], // 7 players
      [3, 3, 4, 5, 5], // 8-9 players
      [3, 4, 4, 5, 5], // 10-11 players
      [3, 4, 4, 5, 5]  // 12-13 players
    ]
    
    const index = Math.min(playerCount - 5, 4)
    return quests[index][Math.min(round - 1, 4)] || 3
  }

  function getTeamSize(): number {
    if (!session) return 3
    return getQuestNumber(session.current_round)
  }

  function isMyTurn(): boolean {
    if (!session || !myEmpId) return false
    return session.leader_emp_id === myEmpId
  }

  function handlePlayerSelect(empId: string) {
    if (!isLeader || actionLoading) return
    
    const teamSize = getTeamSize()
    
    if (selectedTeamIds.includes(empId)) {
      // Deselect
      setSelectedTeamIds(prev => prev.filter(id => id !== empId))
    } else {
      // Select (if not already full)
      if (selectedTeamIds.length < teamSize) {
        setSelectedTeamIds(prev => [...prev, empId])
      }
    }
  }

  async function handleNominateTeam() {
    if (!sessionId || !isLeader || actionLoading) return
    
    const teamSize = getTeamSize()
    if (selectedTeamIds.length !== teamSize) {
      setActionMessage(`请选择 ${teamSize} 名队员`)
      return
    }

    setActionLoading(true)
    setActionMessage(null)
    
    try {
      const result = await nominateTeam(parseInt(sessionId), selectedTeamIds.map(id => parseInt(id)))
      
      if (result.success) {
        setActionMessage('队伍提名成功！等待玩家投票')
        addGameLog('队伍提名', `队长提名了 ${selectedTeamIds.length} 名队员`)
        setSelectedTeamIds([])
        await loadGameData()
      } else {
        setActionMessage(result.error || '提名失败')
      }
    } catch (err) {
      console.error('Nominate team error:', err)
      setActionMessage('提名失败，请重试')
    } finally {
      setActionLoading(false)
    }
  }

  async function handleVote(approve: boolean) {
    if (!sessionId || !myEmpId || hasVoted || actionLoading) return

    setActionLoading(true)
    setActionMessage(null)
    
    try {
      const result = await voteForTeam(
        parseInt(sessionId),
        parseInt(myEmpId),
        approve
      )
      
      if (result.success) {
        setHasVoted(true)
        if (result.data?.vote_passed === true) {
          setActionMessage('投票成功！队伍已通过，进入任务执行阶段')
          addGameLog('投票通过', `队伍获得 ${approve ? '赞成' : '反对'} 票，表决通过`)
        } else if (result.data?.vote_passed === false) {
          setActionMessage('投票成功！队伍被否决，重新提名')
          addGameLog('投票否决', `队伍未获得足够支持，队长轮换`)
        } else {
          setActionMessage('投票成功！等待其他玩家投票')
        }
        await loadVoteStatus()
        await loadGameData()
      } else {
        setActionMessage(result.error || '投票失败')
      }
    } catch (err) {
      console.error('Vote error:', err)
      setActionMessage('投票失败，请重试')
    } finally {
      setActionLoading(false)
    }
  }

  async function handleExecuteQuest(success: boolean) {
    if (!sessionId || !myEmpId || actionLoading) return

    setActionLoading(true)
    setActionMessage(null)
    
    try {
      const result = await executeQuest(
        parseInt(sessionId),
        parseInt(myEmpId),
        success ? 'success' : 'fail'
      )
      
      if (result.success && result.data) {
        const msg = result.data.game_over 
          ? result.data.message 
          : `任务${success ? '成功' : '失败'}！${result.data.message}`
        setActionMessage(msg)
        
        addGameLog('任务执行', `任务 ${success ? '成功 ✓' : '失败 ✗'}`)
        
        if (result.data.game_over) {
          addGameLog('游戏结束', result.data.message)
          // 游戏结束，等待几秒钟后跳转
          setTimeout(() => {
            navigate(`/online/room/${session?.room_id ?? ''}`)
          }, 3000)
        } else {
          await loadGameData()
        }
      } else {
        setActionMessage(result.error || '执行失败')
      }
    } catch (err) {
      console.error('Execute quest error:', err)
      setActionMessage('执行失败，请重试')
    } finally {
      setActionLoading(false)
    }
  }

  async function handleAssassinateMerlin(targetPlayerId: string) {
    if (!sessionId || !myEmpId || actionLoading) return

    setActionLoading(true)
    setActionMessage(null)
    
    try {
      const result = await assassinateMerlin(
        parseInt(sessionId),
        parseInt(myEmpId),
        parseInt(targetPlayerId)
      )
      
      if (result.success && result.data) {
        setActionMessage(result.data.message)
        addGameLog('刺杀行动', result.data.message)
        // 游戏真正结束，等待几秒钟后跳转
        setTimeout(() => {
          navigate(`/online/room/${session?.room_id ?? ''}`)
        }, 5000)
      } else {
        setActionMessage(result.error || '刺杀失败')
      }
    } catch (err) {
      console.error('Assassinate merlin error:', err)
      setActionMessage('刺杀失败，请重试')
    } finally {
      setActionLoading(false)
    }
  }

  async function handleStartNightPhase() {
    if (!sessionId || actionLoading) return

    setActionLoading(true)
    setActionMessage(null)
    
    try {
      const result = await startNightPhase(parseInt(sessionId))
      
      if (result.success && result.data) {
        setActionMessage(result.data.message)
        addGameLog('夜间流程', '游戏开始，进入夜间阶段')
        await loadGameData()
      } else {
        setActionMessage(result.error || '启动失败')
      }
    } catch (err) {
      console.error('Start night phase error:', err)
      setActionMessage('启动失败，请重试')
    } finally {
      setActionLoading(false)
    }
  }

  async function handleMerlinReveal() {
    if (!sessionId || !myEmpId || actionLoading) return

    setActionLoading(true)
    setActionMessage(null)
    
    try {
      const result = await merlinReveal(parseInt(sessionId), parseInt(myEmpId))
      
      if (result.success && result.data) {
        setActionMessage(`${result.data.message} - 邪恶玩家：${result.data.evil_player_ids?.join(', ')}`)
      } else {
        setActionMessage(result.error || '操作失败')
      }
    } catch (err) {
      console.error('Merlin reveal error:', err)
      setActionMessage('操作失败，请重试')
    } finally {
      setActionLoading(false)
    }
  }

  async function handlePercivalReveal() {
    if (!sessionId || !myEmpId || actionLoading) return

    setActionLoading(true)
    setActionMessage(null)
    
    try {
      const result = await percivalReveal(parseInt(sessionId), parseInt(myEmpId))
      
      if (result.success && result.data) {
        setActionMessage(`${result.data.message} - 梅林 ID：${result.data.merlin_id}, 莫甘娜 ID：${result.data.morgana_id}`)
      } else {
        setActionMessage(result.error || '操作失败')
      }
    } catch (err) {
      console.error('Percival reveal error:', err)
      setActionMessage('操作失败，请重试')
    } finally {
      setActionLoading(false)
    }
  }

  async function handleEndNightPhase() {
    if (!sessionId || !myEmpId || actionLoading) return

    setActionLoading(true)
    setActionMessage(null)
    
    try {
      const result = await endNightPhase(parseInt(sessionId), parseInt(myEmpId))
      
      if (result.success && result.data) {
        setActionMessage(result.data.message)
        addGameLog('白天到来', '夜间流程结束，游戏正式开始')
        await loadGameData()
      } else {
        setActionMessage(result.error || '结束失败')
      }
    } catch (err) {
      console.error('End night phase error:', err)
      setActionMessage('结束失败，请重试')
    } finally {
      setActionLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 flex items-center justify-center">
        <div className="text-white text-xl">加载中...</div>
      </div>
    )
  }

  if (error || !session || !room) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 flex items-center justify-center">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 max-w-md mx-4">
          <h2 className="text-2xl font-bold text-white mb-4">错误</h2>
          <p className="text-white/80 mb-6">{error || '游戏不存在'}</p>
          <button
            onClick={() => navigate('/online')}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all"
          >
            返回大厅
          </button>
        </div>
      </div>
    )
  }

  const questNumber = session ? getQuestNumber(session.current_round) : 3
  const teamSize = session ? getTeamSize() : 3
  const isLeader = isMyTurn()
  
  // Check if current player is in the team
  const isInTeam = session?.team_member_ids?.includes(myEmpId!) || false

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            {room.name}
          </h1>
          <p className="text-white/70">
            第 {session.current_round} 轮 - 第 {session.quest_number} 个任务
          </p>
        </div>

        {/* Game Status */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {/* Round Info */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">当前回合</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-white/70">任务人数</span>
                <span className="text-white font-semibold">{teamSize} 人</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/70">失败次数</span>
                <span className="text-red-400 font-semibold">{session.fail_count}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/70">成功次数</span>
                <span className="text-green-400 font-semibold">{session.success_count}</span>
              </div>
            </div>
          </div>

          {/* Leader */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">当前队长</h3>
            {session.leader_emp_id ? (
              <div className="text-white">
                <p className="text-2xl font-bold">👑</p>
                <p className="text-sm text-white/70 mt-2">正在提名队伍</p>
              </div>
            ) : (
              <p className="text-white/70">等待确定队长</p>
            )}
          </div>

          {/* My Role */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">我的身份</h3>
            {myRole ? (
              <div>
                <p className="text-2xl font-bold text-white capitalize">{myRole}</p>
                <p className="text-sm text-white/70 mt-2">
                  {['merlin', 'percival', 'loyalServant'].includes(myRole as string) 
                    ? '✨ 正义阵营' 
                    : '🗡️ 邪恶阵营'}
                </p>
              </div>
            ) : (
              <p className="text-white/70">未知</p>
            )}
          </div>

          {/* Current Phase */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">游戏阶段</h3>
            <div className="text-white">
              <p className="text-xl font-bold mb-2">
                {session.round_state === 'nominating_team' && '📋 提名队伍'}
                {session.round_state === 'voting_team' && '🗳️ 投票表决'}
                {session.round_state === 'executing_quest' && '⚔️ 执行任务'}
                {session.round_state === 'round_complete' && '✅ 回合完成'}
                {session.current_phase === 'night_phase' && '🌙 夜间流程'}
                {session.current_phase === 'game_over' && '🏆 游戏结束'}
                {session.current_phase === 'not_started' && '⏸️ 等待开始'}
              </p>
              <p className="text-sm text-white/70">
                {session.round_state === 'nominating_team' && '队长请选择队员'}
                {session.round_state === 'voting_team' && '所有玩家投票决定'}
                {session.round_state === 'executing_quest' && '队员提交任务结果'}
                {session.round_state === 'round_complete' && '准备下一轮'}
                {session.current_phase === 'night_phase' && '特殊角色行动'}
                {session.current_phase === 'game_over' && '查看最终结果'}
                {session.current_phase === 'not_started' && '房主启动游戏'}
              </p>
            </div>
          </div>
        </div>

        {/* Players Grid */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 mb-8">
          <h3 className="text-xl font-semibold text-white mb-6">玩家列表</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {players.map((player) => {
              const isSelected = selectedTeamIds.includes(player.emp_id)
              const isInCurrentTeam = session.team_member_ids?.includes(player.emp_id)
              const canSelect = isLeader && session.round_state === 'nominating_team' && !actionLoading
              
              return (
                <div
                  key={player.emp_id}
                  onClick={() => canSelect && handlePlayerSelect(player.emp_id)}
                  className={`relative p-4 rounded-xl cursor-pointer transition-all ${
                    player.emp_id === myEmpId
                      ? 'bg-gradient-to-br from-blue-500/30 to-purple-500/30 border-2 border-blue-400'
                      : canSelect
                      ? isSelected
                        ? 'bg-green-500/30 border-2 border-green-400 hover:bg-green-500/40'
                        : 'bg-white/5 border border-white/10 hover:bg-white/10'
                      : isInCurrentTeam
                      ? 'bg-yellow-500/30 border-2 border-yellow-400'
                      : 'bg-white/5 border border-white/10'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-3xl mb-2">
                      {!player.is_alive ? '💀' : player.emp_id === myEmpId ? '👤' : '🎭'}
                    </div>
                    <p className="text-white font-semibold text-sm truncate">
                      {player.emp_id === myEmpId ? '我' : `玩家${player.seat_number}`}
                    </p>
                    {player.emp_id === myEmpId && myRole && (
                      <p className="text-xs text-blue-300 mt-1 capitalize">{String(myRole)}</p>
                    )}
                    {player.emp_id === session.leader_emp_id && (
                      <div className="absolute -top-2 -right-2 text-2xl">👑</div>
                    )}
                    {isSelected && (
                      <div className="absolute -top-1 -left-1 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                        ✓
                      </div>
                    )}
                    {isInCurrentTeam && session.round_state !== 'nominating_team' && (
                      <div className="absolute -top-1 -left-1 bg-yellow-500 text-white text-xs px-2 py-0.5 rounded-full">
                        队
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
          {isLeader && session.round_state === 'nominating_team' && (
            <p className="text-white/60 text-sm mt-4 text-center">
              已选择 {selectedTeamIds.length} / {teamSize} 人
            </p>
          )}
        </div>

        {/* Action Panel */}
        {session.round_state === 'nominating_team' && isLeader && (
          <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-lg rounded-2xl p-6 mb-8 border border-blue-400/30">
            <h3 className="text-xl font-semibold text-white mb-4">👑 你是队长，请提名队伍</h3>
            <p className="text-white/70 mb-4">
              当前需要选择 <span className="text-yellow-400 font-bold">{teamSize}</span> 名玩家执行任务
            </p>
            {actionMessage && (
              <p className={`mb-4 ${actionMessage.includes('成功') ? 'text-green-400' : 'text-red-400'}`}>
                {actionMessage}
              </p>
            )}
            <button
              onClick={handleNominateTeam}
              disabled={selectedTeamIds.length !== teamSize || actionLoading}
              className={`w-full py-3 px-6 rounded-xl font-semibold transition-all ${
                selectedTeamIds.length === teamSize && !actionLoading
                  ? 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white shadow-lg'
                  : 'bg-white/10 text-white/50 cursor-not-allowed'
              }`}
            >
              {actionLoading ? '提交中...' : `确认提名 (${selectedTeamIds.length}/${teamSize})`}
            </button>
          </div>
        )}

        {/* Voting Area */}
        {session.round_state === 'voting_team' && (
          <div className="bg-gradient-to-r from-green-500/20 to-blue-500/20 backdrop-blur-lg rounded-2xl p-6 mb-8 border border-green-400/30">
            <h3 className="text-xl font-semibold text-white mb-4">🗳️ 队伍表决</h3>
            
            {session.team_member_ids && (
              <div className="mb-4">
                <p className="text-white/70 mb-2">提名的队伍：</p>
                <div className="flex flex-wrap gap-2">
                  {session.team_member_ids.map(empId => {
                    const player = players.find(p => p.emp_id === empId)
                    return (
                      <div key={empId} className="bg-yellow-500/20 border border-yellow-400/30 px-3 py-1.5 rounded-lg text-white text-sm">
                        玩家{player?.seat_number}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {voteStatus && (
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-white/70">投票进度</span>
                  <span className="text-white font-semibold">{voteStatus.total_votes} / {voteStatus.total_players}</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
                  <div 
                    className="bg-gradient-to-r from-green-500 to-blue-500 h-full transition-all duration-300"
                    style={{ width: `${(voteStatus.total_votes / voteStatus.total_players) * 100}%` }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-sm">
                  <span className="text-green-400">✓ 赞成：{voteStatus.approve_count}</span>
                  <span className="text-red-400">✗ 反对：{voteStatus.reject_count}</span>
                </div>
              </div>
            )}

            {actionMessage && (
              <p className={`mb-4 ${actionMessage.includes('成功') ? 'text-green-400' : 'text-red-400'}`}>
                {actionMessage}
              </p>
            )}

            {!hasVoted ? (
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => handleVote(true)}
                  disabled={actionLoading}
                  className="py-3 px-6 rounded-xl font-semibold bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg transition-all disabled:opacity-50"
                >
                  ✓ 赞成
                </button>
                <button
                  onClick={() => handleVote(false)}
                  disabled={actionLoading}
                  className="py-3 px-6 rounded-xl font-semibold bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white shadow-lg transition-all disabled:opacity-50"
                >
                  ✗ 反对
                </button>
              </div>
            ) : (
              <div className="text-center py-4 text-white/70">
                <p>✓ 你已投票，等待其他玩家</p>
              </div>
            )}
          </div>
        )}

        {/* Quest Execution Area */}
        {session.round_state === 'executing_quest' && isInTeam && (
          <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 backdrop-blur-lg rounded-2xl p-6 mb-8 border border-orange-400/30">
            <h3 className="text-xl font-semibold text-white mb-4">⚔️ 执行任务</h3>
            <p className="text-white/70 mb-4">你是被选中的队员，请选择任务结果</p>
            {actionMessage && (
              <p className={`mb-4 ${actionMessage.includes('成功') ? 'text-green-400' : 'text-red-400'}`}>
                {actionMessage}
              </p>
            )}
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => handleExecuteQuest(true)}
                disabled={actionLoading}
                className="py-3 px-6 rounded-xl font-semibold bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg transition-all disabled:opacity-50"
              >
                ✓ 任务成功
              </button>
              <button
                onClick={() => handleExecuteQuest(false)}
                disabled={actionLoading}
                className="py-3 px-6 rounded-xl font-semibold bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white shadow-lg transition-all disabled:opacity-50"
              >
                ✗ 任务失败
              </button>
            </div>
          </div>
        )}

        {/* Game Over - Assassination Phase */}
        {session?.current_phase === 'game_over' && session.winner_faction === 'red' && myRole === 'assassin' && (
          <div className="bg-gradient-to-r from-red-500/30 to-pink-500/30 backdrop-blur-lg rounded-2xl p-6 mb-8 border border-red-400/50">
            <h3 className="text-2xl font-bold text-white mb-4">🗡️ 刺客行动！</h3>
            <p className="text-white/80 mb-4">
              红方已赢得 3 个任务胜利！现在由你决定最终胜负。
            </p>
            <div className="bg-black/20 rounded-xl p-4 mb-6">
              <p className="text-yellow-400 font-semibold mb-2">
                📜 规则说明
              </p>
              <ul className="text-white/70 text-sm space-y-1">
                <li>• 如果你刺杀的是 <span className="text-purple-400 font-bold">梅林</span> → 红方反败为胜</li>
                <li>• 如果你刺杀的是其他玩家 → 蓝方获胜</li>
                <li>• 只有你可以看到此界面，请谨慎选择</li>
              </ul>
            </div>
            {actionMessage && (
              <p className="mb-4 text-white font-semibold">
                {actionMessage}
              </p>
            )}
            <p className="text-white/80 mb-4 font-semibold">选择要刺杀的目标：</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {players.filter(p => p.is_alive).map((player) => (
                <button
                  key={player.emp_id}
                  onClick={() => handleAssassinateMerlin(player.emp_id)}
                  disabled={actionLoading}
                  className="group p-4 rounded-xl bg-gradient-to-br from-red-500/20 to-pink-500/20 border-2 border-red-400/30 hover:border-red-400 hover:bg-red-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">🎯</div>
                  <p className="text-white font-semibold text-sm">
                    {player.emp_id === myEmpId ? '我' : `玩家${player.seat_number}`}
                  </p>
                  {player.emp_id === session.leader_emp_id && (
                    <p className="text-xs text-yellow-400 mt-1">👑 队长</p>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Game Over - Result Display */}
        {session?.current_phase === 'game_over' && session.winner_faction && (
          <div className={`backdrop-blur-lg rounded-2xl p-8 mb-8 border-2 ${
            session.winner_faction === 'blue' 
              ? 'bg-gradient-to-r from-blue-500/30 to-purple-500/30 border-blue-400' 
              : 'bg-gradient-to-r from-red-500/30 to-pink-500/30 border-red-400'
          }`}>
            <div className="text-center">
              <div className="text-6xl mb-4">
                {session.winner_faction === 'blue' ? '🎉' : '⚔️'}
              </div>
              <h3 className="text-3xl font-bold text-white mb-2">
                {session.winner_faction === 'blue' ? '蓝方获胜！' : '等待刺杀结果...'}
              </h3>
              <p className="text-white/80 mb-4">
                任务统计：{session.success_count} 次成功，{session.fail_count} 次失败
              </p>
              {session.quest_results && (
                <div className="flex justify-center gap-2 flex-wrap">
                  {Object.entries(session.quest_results).map(([key, result]) => (
                    <span
                      key={key}
                      className={`px-4 py-2 rounded-full font-bold ${
                        result === 'success' 
                          ? 'bg-green-500 text-white' 
                          : 'bg-red-500 text-white'
                      }`}
                    >
                      任务{parseInt(key) + 1}: {result === 'success' ? '✓' : '✗'}
                    </span>
                  ))}
                </div>
              )}
              <p className="text-white/60 mt-6 text-sm">
                即将返回房间...
              </p>
            </div>
          </div>
        )}

        {/* Night Phase UI */}
        {session?.current_phase === 'night_phase' && (
          <div className="bg-gradient-to-r from-indigo-500/20 to-purple-500/20 backdrop-blur-lg rounded-2xl p-6 mb-8 border border-indigo-400/30">
            <h3 className="text-2xl font-bold text-white mb-4">🌙 夜间流程</h3>
            
            <div className="bg-black/20 rounded-xl p-4 mb-6">
              <p className="text-white/70 text-sm mb-2">当前阶段说明：</p>
              <ul className="text-white/80 text-sm space-y-1">
                <li>1️⃣ <span className="text-purple-400">梅林</span> 睁眼，识别邪恶阵营成员</li>
                <li>2️⃣ <span className="text-blue-400">派西维尔</span> 睁眼，识别梅林和莫甘娜</li>
                <li>3️⃣ <span className="text-green-400">房主/队长</span> 结束夜间流程，开始游戏</li>
              </ul>
            </div>
            
            {myRole === 'merlin' && (
              <div className="mb-6">
                <div className="bg-purple-500/20 border border-purple-400/30 rounded-xl p-4 mb-4">
                  <p className="text-purple-300 font-bold mb-2">🔮 梅林（你）的行动</p>
                  <p className="text-white/80 text-sm mb-4">
                    作为梅林，你需要识别所有邪恶阵营玩家，以便在游戏中做出正确判断。
                    <br/>
                    <span className="text-yellow-400 text-xs">注意：你的身份必须保密！</span>
                  </p>
                  <button
                    onClick={handleMerlinReveal}
                    disabled={actionLoading}
                    className="w-full py-3 px-6 rounded-xl font-semibold bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white shadow-lg transition-all disabled:opacity-50"
                  >
                    👁️ 查看邪恶阵营成员
                  </button>
                </div>
              </div>
            )}
            
            {myRole === 'percival' && (
              <div className="mb-6">
                <div className="bg-blue-500/20 border border-blue-400/30 rounded-xl p-4 mb-4">
                  <p className="text-blue-300 font-bold mb-2">🛡️ 派西维尔（你）的行动</p>
                  <p className="text-white/80 text-sm mb-4">
                    作为派西维尔，你需要识别出真正的梅林，并保护他/她不被刺杀。
                    <br/>
                    <span className="text-yellow-400 text-xs">注意：莫甘娜会伪装成梅林混淆你！</span>
                  </p>
                  <button
                    onClick={handlePercivalReveal}
                    disabled={actionLoading}
                    className="w-full py-3 px-6 rounded-xl font-semibold bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white shadow-lg transition-all disabled:opacity-50"
                  >
                    👁️ 查看梅林和莫甘娜
                  </button>
                </div>
              </div>
            )}
            
            {myRole !== 'merlin' && myRole !== 'percival' && (
              <div className="bg-white/5 rounded-xl p-4 mb-6">
                <p className="text-white/70 mb-2">🎭 你的角色</p>
                <p className="text-white/80 text-sm">
                  你是一名忠诚的侍从，请在白天阶段帮助正义阵营获胜。
                  <br/>
                  <span className="text-white/50 text-xs">等待特殊角色完成夜间行动...</span>
                </p>
              </div>
            )}
            
            {((session.room_leader_id ?? '') === myEmpId || (session.current_leader_id ?? '') === myEmpId) && (
              <div className="mt-6">
                <div className="bg-green-500/20 border border-green-400/30 rounded-xl p-4">
                  <p className="text-green-300 font-bold mb-2">🌅 房主/队长操作</p>
                  <p className="text-white/70 text-sm mb-4">
                    当所有特殊角色完成行动后，结束夜间流程并开始游戏。
                  </p>
                  <button
                    onClick={handleEndNightPhase}
                    disabled={actionLoading}
                    className="w-full py-3 px-6 rounded-xl font-semibold bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg transition-all disabled:opacity-50"
                  >
                    🌅 结束夜间流程，开始游戏
                  </button>
                </div>
              </div>
            )}
            
            {actionMessage && (
              <p className="mt-4 text-white font-semibold">{actionMessage}</p>
            )}
          </div>
        )}

        {/* Start Night Phase Button (for game leader) */}
        {session?.current_phase === 'not_started' && ((session.room_leader_id ?? '') === myEmpId || (session.current_leader_id ?? '') === myEmpId) && (
          <div className="bg-gradient-to-r from-indigo-500/20 to-purple-500/20 backdrop-blur-lg rounded-2xl p-6 mb-8 border border-indigo-400/30">
            <h3 className="text-xl font-semibold text-white mb-4">🌙 准备开始游戏</h3>
            <p className="text-white/70 mb-4">所有玩家已就位，开始夜间流程</p>
            {actionMessage && (
              <p className={`mb-4 ${actionMessage.includes('成功') ? 'text-green-400' : 'text-red-400'}`}>
                {actionMessage}
              </p>
            )}
            <button
              onClick={handleStartNightPhase}
              disabled={actionLoading}
              className="w-full py-3 px-6 rounded-xl font-semibold bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white shadow-lg transition-all disabled:opacity-50"
            >
              🎮 开始夜间流程
            </button>
          </div>
        )}

        {/* Game Log Panel */}
        <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 mb-8 border border-white/10">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <span>📜</span> 游戏日志
          </h3>
          {gameLogs.length === 0 ? (
            <p className="text-white/50 text-sm">暂无游戏记录</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {gameLogs.map((log) => (
                <div key={log.id} className="flex gap-3 text-sm">
                  <span className="text-white/40 w-16 flex-shrink-0">{log.timestamp}</span>
                  <span className="text-white/70 w-24 flex-shrink-0 font-medium">{log.event}</span>
                  <span className="text-white/90 flex-1">{log.description}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Back Button */}
        <div className="text-center">
          <button
            onClick={() => navigate(`/online/room/${session.room_id}`)}
            className="bg-white/10 backdrop-blur-lg text-white px-8 py-3 rounded-xl font-semibold hover:bg-white/20 transition-all"
          >
            返回房间
          </button>
        </div>
      </div>
    </div>
  )
}
